import { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import { hablar } from '../utils/audioHelper'

const API = process.env.REACT_APP_API_URL

const COLOR_NIVEL = { 'A1-A2': '#22C55E', 'B1': '#8B5CF6', 'B2': '#F59E0B', 'C1': '#EF4444' }

function mezclar(arr) {
  return [...arr].sort(() => Math.random() - 0.5)
}

/* Busca el termino (sin importar mayusculas/minusculas) dentro
   del ejemplo y lo separa en 3 partes para poder pintar un
   espacio en blanco donde deberia ir. Si por alguna razon no lo
   encuentra, devuelve el ejemplo completo sin blanco. */
function blanquear(ejemplo, termino) {
  const idx = ejemplo.toLowerCase().indexOf(termino.toLowerCase())
  if (idx === -1) return { antes: ejemplo, medio: null, despues: '' }
  return {
    antes: ejemplo.slice(0, idx),
    medio: ejemplo.slice(idx, idx + termino.length),
    despues: ejemplo.slice(idx + termino.length)
  }
}

const PASOS = ['flashcards', 'match', 'opcion_multiple', 'completar']
const NUM_PASO = { flashcards: 1, match: 2, opcion_multiple: 3, completar: 4 }
const TITULO_PASO = {
  flashcards: 'Conoce las palabras de hoy',
  match: 'Empareja cada termino con su significado',
  opcion_multiple: '¿Que significa cada uno?',
  completar: 'Completa la frase'
}

/* Pagina de "Reto diario": un paquete de 5 palabras/frases de
   slang en ingles compartido por todos los usuarios cada dia
   (rotacion determinista en el backend, ver api/src/routes/retos.js),
   con 4 mini-actividades (flashcards, emparejar, opcion multiple,
   completar la frase) y una racha estilo Duolingo. */
export default function RetoDiario({ usuario }) {
  const [cargando,          setCargando]          = useState(true)
  const [paquete,           setPaquete]           = useState([])
  const [xpPaquete,         setXpPaquete]         = useState(0)
  const [completadoHoy,     setCompletadoHoy]     = useState(false)
  const [rachaDias,         setRachaDias]         = useState(0)
  const [rachaMaxima,       setRachaMaxima]       = useState(0)
  const [retosCompletados,  setRetosCompletados]  = useState(0)
  const [error,             setError]             = useState(null)

  const [paso,        setPaso]        = useState('flashcards')
  const [indiceFlash, setIndiceFlash] = useState(0)

  // ── Emparejar (match) ──
  const [emparejados,  setEmparejados]  = useState(new Set())
  const [terminoSel,   setTerminoSel]   = useState(null)
  const [defSel,       setDefSel]       = useState(null)
  const [errorMatch,   setErrorMatch]   = useState(false)

  // ── Opcion multiple ──
  const [indiceMC,   setIndiceMC]   = useState(0)
  const [aciertosMC, setAciertosMC] = useState(0)
  const [mcResp,     setMcResp]     = useState(null)
  const [mcValidado, setMcValidado] = useState(false)

  // ── Completar la frase ──
  const [indiceFrase,   setIndiceFrase]   = useState(0)
  const [aciertosFrase, setAciertosFrase] = useState(0)
  const [fraseResp,     setFraseResp]     = useState(null)
  const [fraseValidado, setFraseValidado] = useState(false)

  const [enviando,  setEnviando]  = useState(false)
  const [resultado, setResultado] = useState(null)

  // ── My Word Bank (palabras ya vistas en retos anteriores) ──
  const [mostrarBanco,   setMostrarBanco]   = useState(false)
  const [cargandoBanco,  setCargandoBanco]  = useState(false)
  const [bancoPalabras,  setBancoPalabras]  = useState(null)
  const [filtroBanco,    setFiltroBanco]    = useState('Todos')
  const [errorBanco,     setErrorBanco]     = useState(null)

  const cargar = useCallback(() => {
    if (!usuario?.id) { setCargando(false); return }
    setCargando(true)
    setError(null)
    axios.get(`${API}/retos/hoy/${usuario.id}`).then(res => {
      const d = res.data
      setPaquete(d.paquete || [])
      setXpPaquete(d.xp_total_paquete || 0)
      setCompletadoHoy(!!d.completado_hoy)
      setRachaDias(d.racha_dias || 0)
      setRachaMaxima(d.racha_maxima || 0)
      setRetosCompletados(d.retos_completados || 0)
    }).catch(err => {
      console.error('Error cargando reto diario:', err)
      setError('No pudimos cargar el reto de hoy. Intenta de nuevo mas tarde.')
    }).finally(() => setCargando(false))
  }, [usuario])

  useEffect(() => { cargar() }, [cargar])

  // Reinicia el estado de la mini-lecciones cada vez que llega un paquete nuevo
  useEffect(() => {
    setPaso('flashcards'); setIndiceFlash(0)
    setEmparejados(new Set()); setTerminoSel(null); setDefSel(null); setErrorMatch(false)
    setIndiceMC(0); setAciertosMC(0); setMcResp(null); setMcValidado(false)
    setIndiceFrase(0); setAciertosFrase(0); setFraseResp(null); setFraseValidado(false)
    setResultado(null)
  }, [paquete])

  const definicionesBarajadas = useMemo(() => mezclar(paquete), [paquete])

  const preguntasMC = useMemo(() => paquete.map(term => {
    const otras = paquete.filter(p => p.id !== term.id)
    const distractores = mezclar(otras).slice(0, 2).map(o => ({ id: o.id, texto: o.definicion }))
    const opciones = mezclar([{ id: term.id, texto: term.definicion }, ...distractores])
    return { termino: term, opciones }
  }), [paquete])

  const preguntasFrase = useMemo(() => paquete.map(term => {
    const partes = blanquear(term.ejemplo, term.termino)
    const opciones = mezclar(paquete.map(p => p.termino))
    return { termino: term, partes, opciones }
  }), [paquete])

  // Resuelve la logica de emparejar cuando hay una seleccion de ambos lados
  useEffect(() => {
    if (!terminoSel || !defSel) return
    if (terminoSel === defSel) {
      setEmparejados(prev => new Set(prev).add(terminoSel))
      setTerminoSel(null); setDefSel(null); setErrorMatch(false)
    } else {
      setErrorMatch(true)
      const timer = setTimeout(() => {
        setTerminoSel(null); setDefSel(null); setErrorMatch(false)
      }, 700)
      return () => clearTimeout(timer)
    }
  }, [terminoSel, defSel])

  function elegirMC(op) {
    if (mcValidado) return
    setMcResp(op.id)
    setMcValidado(true)
    if (op.id === preguntasMC[indiceMC].termino.id) setAciertosMC(a => a + 1)
    setTimeout(() => {
      if (indiceMC < preguntasMC.length - 1) {
        setIndiceMC(i => i + 1); setMcResp(null); setMcValidado(false)
      } else {
        setPaso('completar')
      }
    }, 1100)
  }

  function elegirFrase(opTexto) {
    if (fraseValidado) return
    setFraseResp(opTexto)
    setFraseValidado(true)
    if (opTexto === preguntasFrase[indiceFrase].termino.termino) setAciertosFrase(a => a + 1)
    setTimeout(() => {
      if (indiceFrase < preguntasFrase.length - 1) {
        setIndiceFrase(i => i + 1); setFraseResp(null); setFraseValidado(false)
      } else {
        setPaso('resumen')
      }
    }, 1100)
  }

  async function completarReto() {
    if (enviando || !usuario?.id) return
    setEnviando(true)
    setError(null)
    try {
      const totalPreguntas = preguntasMC.length + preguntasFrase.length
      const aciertos = aciertosMC + aciertosFrase
      const res = await axios.post(`${API}/retos/completar`, {
        usuario_id: usuario.id, aciertos, total_preguntas: totalPreguntas
      })
      setResultado(res.data)
      setCompletadoHoy(true)
      setRachaDias(res.data.racha_dias || 0)
      setRachaMaxima(res.data.racha_maxima || 0)
      setRetosCompletados(res.data.retos_completados || 0)
    } catch (err) {
      console.error('Error completando reto:', err)
      setError('No pudimos guardar tu progreso. Intenta de nuevo.')
    } finally {
      setEnviando(false)
    }
  }

  function abrirBanco() {
    setMostrarBanco(true)
    if (bancoPalabras !== null || !usuario?.id) return
    setCargandoBanco(true)
    setErrorBanco(null)
    axios.get(`${API}/retos/vistas/${usuario.id}`).then(res => {
      setBancoPalabras(res.data.datos || [])
    }).catch(err => {
      console.error('Error cargando My Word Bank:', err)
      setErrorBanco('No pudimos cargar tus palabras. Intenta de nuevo.')
    }).finally(() => setCargandoBanco(false))
  }

  function formatearFecha(fechaISO) {
    if (!fechaISO) return ''
    const [anio, mes, dia] = fechaISO.split('-')
    const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
    return `${parseInt(dia, 10)} ${meses[parseInt(mes, 10) - 1]}`
  }

  const NIVELES_BANCO = ['Todos', 'A1-A2', 'B1', 'B2', 'C1']

  // ── Vista de "My Word Bank" ─────────────────────────────────
  if (mostrarBanco) {
    const palabras = bancoPalabras || []
    const visibles = filtroBanco === 'Todos' ? palabras : palabras.filter(p => p.nivel === filtroBanco)

    return (
      <div style={{ animation: 'fadeIn .3s ease both', maxWidth: 720 }}>
        <button onClick={() => setMostrarBanco(false)}
          style={{ background: 'none', border: 'none', color: 'var(--azul)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 16 }}>
          ← Volver al reto
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800 }}>📚 My Word Bank</h1>
            <p style={{ color: 'var(--gris-texto)', fontSize: 14, marginTop: 4 }}>
              Todas las palabras y frases que has aprendido en tus retos diarios.
            </p>
          </div>
          {!!palabras.length && (
            <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 12, padding: '10px 16px', textAlign: 'center', minWidth: 78 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--azul)' }}>{palabras.length}</div>
              <div style={{ fontSize: 10, color: 'var(--gris-texto)' }}>palabras</div>
            </div>
          )}
        </div>

        {errorBanco && (
          <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
            {errorBanco}
          </div>
        )}

        {!cargandoBanco && !!palabras.length && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {NIVELES_BANCO.map(n => (
              <button key={n} onClick={() => setFiltroBanco(n)}
                style={{
                  padding: '7px 16px', borderRadius: 999, border: '1.5px solid ' + (filtroBanco === n ? 'var(--azul)' : 'var(--gris-borde)'),
                  background: filtroBanco === n ? 'var(--azul)' : '#fff', color: filtroBanco === n ? '#fff' : 'var(--gris-texto)',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer'
                }}>
                {n}
              </button>
            ))}
          </div>
        )}

        {cargandoBanco && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
            Cargando tu banco de palabras...
          </div>
        )}

        {!cargandoBanco && !palabras.length && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>📚</div>
            <p>Todavía no has completado ningún reto diario.</p>
            <p style={{ fontSize: 13, marginTop: 4 }}>Las palabras que aprendas aquí van a aparecer.</p>
          </div>
        )}

        {!cargandoBanco && !!palabras.length && !visibles.length && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gris-texto)' }}>
            <p>No tienes palabras de este nivel todavía.</p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {visibles.map(term => {
            const colorNivel = COLOR_NIVEL[term.nivel] || 'var(--azul)'
            return (
              <div key={term.id} style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 14, padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ background: colorNivel, color: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 999 }}>
                    {term.nivel}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--gris-texto)' }}>
                    {term.tipo === 'frase' ? 'Frase de slang' : 'Palabra de slang'}
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--gris-texto)' }}>
                    Visto el {formatearFecha(term.fecha_vista)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--azul-oscuro)' }}>{term.termino}</h3>
                  <button onClick={() => hablar(term.termino)}
                    style={{ background: '#E8EFFE', border: 'none', borderRadius: '50%', width: 26, height: 26, cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>
                    🔊
                  </button>
                </div>
                <p style={{ fontSize: 13.5, color: '#1A1A2E' }}>{term.definicion}</p>
                <p style={{ fontSize: 12.5, color: 'var(--gris-texto)', marginBottom: 8 }}>{term.definicion_traduccion}</p>
                <div style={{ background: '#F8FAFF', borderRadius: 10, padding: 10 }}>
                  <p style={{ fontSize: 12.5, color: '#1A1A2E', fontStyle: 'italic' }}>"{term.ejemplo}"</p>
                  <p style={{ fontSize: 11.5, color: 'var(--gris-texto)', marginTop: 2 }}>{term.ejemplo_traduccion}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (cargando) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
        Cargando el reto de hoy...
      </div>
    )
  }

  if (!paquete.length) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>🔥</div>
        <p>Todavia no hay retos diarios cargados. ¡Vuelve pronto!</p>
      </div>
    )
  }

  const Cabecera = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>🔥 Reto diario</h1>
        <p style={{ color: 'var(--gris-texto)', fontSize: 14, marginTop: 4 }}>
          5 palabras y frases de slang en ingles, cada dia, para todos los estudiantes.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 12, padding: '10px 16px', textAlign: 'center', minWidth: 78 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#F59E0B' }}>🔥 {rachaDias}</div>
          <div style={{ fontSize: 10, color: 'var(--gris-texto)' }}>racha actual</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 12, padding: '10px 16px', textAlign: 'center', minWidth: 78 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--azul)' }}>🏆 {retosCompletados}</div>
          <div style={{ fontSize: 10, color: 'var(--gris-texto)' }}>completados</div>
        </div>
        <button onClick={abrirBanco}
          style={{ background: 'var(--azul)', border: 'none', borderRadius: 12, padding: '10px 16px', color: '#fff',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', height: '100%' }}>
          📚 My Word Bank
        </button>
      </div>
    </div>
  )

  const listaPalabrasHoy = (
    <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 16, padding: 18, marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gris-texto)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: .5 }}>
        Palabras de hoy
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {paquete.map(term => (
          <span key={term.id} style={{ background: (COLOR_NIVEL[term.nivel] || 'var(--azul)') + '22', color: COLOR_NIVEL[term.nivel] || 'var(--azul)', fontSize: 13, fontWeight: 700, padding: '5px 12px', borderRadius: 999 }}>
            {term.termino}
          </span>
        ))}
      </div>
    </div>
  )

  // ── Ya completado hoy ──────────────────────────────────────
  if (completadoHoy) {
    return (
      <div style={{ animation: 'fadeIn .3s ease both', maxWidth: 640 }}>
        {Cabecera}
        <div style={{ background: 'linear-gradient(135deg,#065F46,#059669)', color: '#fff', borderRadius: 16, padding: 26, textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 42, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>¡Ya completaste el reto de hoy!</div>
          <div style={{ fontSize: 13, opacity: .85 }}>
            {resultado?.xp_ganado ? `+${resultado.xp_ganado} XP ganados. ` : ''}
            Vuelve mañana para un paquete nuevo y mantener tu racha 🔥 ({rachaDias} {rachaDias === 1 ? 'dia' : 'dias'}, mejor racha: {rachaMaxima}).
          </div>
        </div>
        {listaPalabrasHoy}
      </div>
    )
  }

  return (
    <div style={{ animation: 'fadeIn .3s ease both', maxWidth: 640 }}>
      {Cabecera}

      {error && (
        <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {paso !== 'resumen' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--azul)' }}>Paso {NUM_PASO[paso]} de 4</span>
          <div style={{ flex: 1, height: 6, background: 'var(--gris-borde)', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ width: `${(NUM_PASO[paso] - 1) / 4 * 100}%`, height: '100%', background: 'var(--dorado)', borderRadius: 999, transition: 'width .3s ease' }} />
          </div>
        </div>
      )}

      {paso !== 'resumen' && (
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>{TITULO_PASO[paso]}</h2>
      )}

      {/* ── Paso 1: Flashcards ── */}
      {paso === 'flashcards' && (() => {
        const term = paquete[indiceFlash]
        const colorNivel = COLOR_NIVEL[term.nivel] || 'var(--azul)'
        return (
          <div>
            <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 18, padding: 26, marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{ background: colorNivel, color: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 999 }}>
                  {term.nivel}
                </span>
                <span style={{ fontSize: 11, color: 'var(--gris-texto)' }}>
                  {term.tipo === 'frase' ? 'Frase de slang' : 'Palabra de slang'}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gris-texto)' }}>{indiceFlash + 1} / {paquete.length}</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--azul-oscuro)' }}>{term.termino}</h2>
                <button onClick={() => hablar(term.termino)}
                  style={{ background: '#E8EFFE', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: 15, flexShrink: 0 }}>
                  🔊
                </button>
              </div>

              <p style={{ fontSize: 15, color: '#1A1A2E', marginBottom: 4 }}>{term.definicion}</p>
              <p style={{ fontSize: 13, color: 'var(--gris-texto)', marginBottom: 16 }}>{term.definicion_traduccion}</p>

              <div style={{ background: '#F8FAFF', borderRadius: 12, padding: 14, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 14, color: '#1A1A2E', fontStyle: 'italic' }}>"{term.ejemplo}"</p>
                  <p style={{ fontSize: 12, color: 'var(--gris-texto)', marginTop: 4 }}>{term.ejemplo_traduccion}</p>
                </div>
                <button onClick={() => hablar(term.ejemplo)}
                  style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: 13, flexShrink: 0 }}>
                  🔊
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              {indiceFlash > 0 && (
                <button onClick={() => setIndiceFlash(i => i - 1)}
                  style={{ padding: '13px 20px', borderRadius: 12, border: '1.5px solid var(--gris-borde)', background: '#fff', color: 'var(--negro)', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  ← Atras
                </button>
              )}
              <button
                onClick={() => indiceFlash < paquete.length - 1 ? setIndiceFlash(i => i + 1) : setPaso('match')}
                style={{ flex: 1, padding: '13px', borderRadius: 12, border: 'none', background: 'var(--dorado)', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                {indiceFlash < paquete.length - 1 ? 'Siguiente →' : 'Empezar actividades →'}
              </button>
            </div>
          </div>
        )
      })()}

      {/* ── Paso 2: Emparejar ── */}
      {paso === 'match' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 18 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {paquete.map(term => {
                const hecho = emparejados.has(term.id)
                const activo = terminoSel === term.id
                return (
                  <button key={term.id} disabled={hecho}
                    onClick={() => setTerminoSel(term.id)}
                    style={{
                      padding: '12px 10px', borderRadius: 10, fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, textAlign: 'left',
                      cursor: hecho ? 'default' : 'pointer',
                      background: hecho ? '#DCFCE7' : (activo ? (errorMatch ? '#FEE2E2' : '#EEF2FF') : '#fff'),
                      border: '1.5px solid ' + (hecho ? '#22C55E' : activo ? (errorMatch ? '#EF4444' : 'var(--azul)') : 'var(--gris-borde)'),
                      color: hecho ? '#166534' : '#1A1A2E', opacity: hecho ? 0.7 : 1
                    }}>
                    {term.termino} {hecho ? '✓' : ''}
                  </button>
                )
              })}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {definicionesBarajadas.map(term => {
                const hecho = emparejados.has(term.id)
                const activo = defSel === term.id
                return (
                  <button key={term.id} disabled={hecho}
                    onClick={() => setDefSel(term.id)}
                    style={{
                      padding: '12px 10px', borderRadius: 10, fontFamily: 'inherit', fontSize: 12.5, textAlign: 'left',
                      cursor: hecho ? 'default' : 'pointer',
                      background: hecho ? '#DCFCE7' : (activo ? (errorMatch ? '#FEE2E2' : '#EEF2FF') : '#fff'),
                      border: '1.5px solid ' + (hecho ? '#22C55E' : activo ? (errorMatch ? '#EF4444' : 'var(--azul)') : 'var(--gris-borde)'),
                      color: hecho ? '#166534' : '#1A1A2E', opacity: hecho ? 0.7 : 1
                    }}>
                    {term.definicion_traduccion}
                  </button>
                )
              })}
            </div>
          </div>
          {emparejados.size === paquete.length && (
            <button onClick={() => setPaso('opcion_multiple')}
              style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'var(--dorado)', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              ¡Todo emparejado! Continuar →
            </button>
          )}
        </div>
      )}

      {/* ── Paso 3: Opcion multiple ── */}
      {paso === 'opcion_multiple' && (() => {
        const p = preguntasMC[indiceMC]
        return (
          <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 18, padding: 24 }}>
            <p style={{ fontSize: 12, color: 'var(--gris-texto)', marginBottom: 6 }}>Pregunta {indiceMC + 1} de {preguntasMC.length}</p>
            <p style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
              ¿Que significa <span style={{ color: 'var(--azul)' }}>"{p.termino.termino}"</span>?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {p.opciones.map(op => {
                const esCorrecta = op.id === p.termino.id
                const esElegida = mcResp === op.id
                let fondo = '#fff', borde = 'var(--gris-borde)'
                if (mcValidado && esCorrecta) { fondo = '#DCFCE7'; borde = '#22C55E' }
                else if (mcValidado && esElegida && !esCorrecta) { fondo = '#FEE2E2'; borde = '#EF4444' }
                return (
                  <button key={op.id} onClick={() => elegirMC(op)} disabled={mcValidado}
                    style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 10, border: '1.5px solid ' + borde,
                      background: fondo, color: '#1A1A2E', fontFamily: 'inherit', fontSize: 13.5, cursor: mcValidado ? 'default' : 'pointer' }}>
                    {op.texto}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* ── Paso 4: Completar la frase ── */}
      {paso === 'completar' && (() => {
        const p = preguntasFrase[indiceFrase]
        return (
          <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 18, padding: 24 }}>
            <p style={{ fontSize: 12, color: 'var(--gris-texto)', marginBottom: 6 }}>Frase {indiceFrase + 1} de {preguntasFrase.length}</p>
            <div style={{ background: '#F8FAFF', borderRadius: 12, padding: 16, marginBottom: 16, fontSize: 15, lineHeight: 1.6 }}>
              {p.partes.medio
                ? <>{p.partes.antes}<strong style={{ background: '#E8EFFE', padding: '2px 10px', borderRadius: 6 }}>____</strong>{p.partes.despues}</>
                : p.partes.antes}
            </div>
            <p style={{ fontSize: 12, color: 'var(--gris-texto)', marginBottom: 12 }}>
              {p.partes.medio ? '¿Cual palabra o frase completa el espacio?' : '¿Cual palabra o frase de hoy corresponde a este ejemplo?'}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {p.opciones.map(opTexto => {
                const esCorrecta = opTexto === p.termino.termino
                const esElegida = fraseResp === opTexto
                let fondo = '#fff', borde = 'var(--gris-borde)'
                if (fraseValidado && esCorrecta) { fondo = '#DCFCE7'; borde = '#22C55E' }
                else if (fraseValidado && esElegida && !esCorrecta) { fondo = '#FEE2E2'; borde = '#EF4444' }
                return (
                  <button key={opTexto} onClick={() => elegirFrase(opTexto)} disabled={fraseValidado}
                    style={{ padding: '10px 14px', borderRadius: 999, border: '1.5px solid ' + borde,
                      background: fondo, color: '#1A1A2E', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: fraseValidado ? 'default' : 'pointer' }}>
                    {opTexto}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* ── Resumen final ── */}
      {paso === 'resumen' && (
        <div>
          <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 18, padding: 26, textAlign: 'center', marginBottom: 18 }}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>🎉</div>
            <h2 style={{ fontSize: 19, fontWeight: 800, marginBottom: 6 }}>¡Terminaste el reto de hoy!</h2>
            <p style={{ fontSize: 14, color: 'var(--gris-texto)', marginBottom: 18 }}>
              Acertaste {aciertosMC + aciertosFrase} de {preguntasMC.length + preguntasFrase.length} preguntas.
            </p>
            {listaPalabrasHoy}
            <button onClick={completarReto} disabled={enviando}
              style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', fontFamily: 'inherit',
                fontSize: 15, fontWeight: 700, cursor: enviando ? 'not-allowed' : 'pointer',
                background: enviando ? '#CBD5E1' : 'var(--dorado)', color: '#fff' }}>
              {enviando ? 'Guardando...' : `Reclamar +${xpPaquete} XP`}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
